import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import RunThreadEntryPoint from '@databox/ember-integrations/lib/components/Ai/Routines/RunThreadEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

interface GenieRoutinesRunSignature {
	Element: HTMLDivElement;
	Args: {
		routineId: string;
		runId: string;
	};
}

export default class GenieRoutinesRunComponent extends Component<GenieRoutinesRunSignature> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;

	@action
	setupView(node: HTMLElement) {
		mountReactRoot(this, node, () => this.renderRun());
	}

	@action
	teardownView() {
		this.aiChatService.clearRunThreadIdGetter();
		unmountReactRoot(this);
	}

	// Switching runs from the breadcrumb changes only @runId, so re-render to let the new prop flow in.
	@action
	handleRunChange() {
		this.renderRun();
	}

	@action
	goToRoutine() {
		this.aiChatService.onNavigateToRoutine(this.args.routineId);
	}

	@action
	openRun(run: { id: string }) {
		this.aiChatService.onNavigateToRun(this.args.routineId, run.id);
	}

	private renderRun() {
		if (!this.reactElement) {
			return;
		}

		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken!,
			dataToken: dataToken!,
			spaceId: this.accountService.id,
			user: {
				id: Number(this.currentUserService.id),
				name: this.currentUserService.displayName,
			},
			trackingOrigin: this.aiChatService.trackingOrigin,
			showToast: this.aiChatService.showToast,
			onActionClick: this.aiChatService.onActionClick,
			featureConfig: this.aiChatService.featureConfig,
			routineId: this.args.routineId,
			runId: this.args.runId,
			onBack: this.goToRoutine,
			onSelectRun: this.openRun,
			// A run can create another routine; its routine-view card opens through this handler.
			onNavigateToRoutine: this.aiChatService.onNavigateToRoutine,
			onUpgrade: this.aiChatService.onUpgrade,
			onAddCredits: this.aiChatService.onAddCredits,
			// Lets ai-chat hold back the answer-ready toast for the run's thread while it is on screen.
			onGetThreadId: this.aiChatService.registerRunThreadIdGetter,
			// The native app has no sidebar to receive run events, and its chat must not pop the keyboard.
			subscribeToAiEvents: this.aiChatService.subscribeToAiEvents,
			onClose: this.navigationService.isMobileApp ? this.goToRoutine : undefined,
		};

		this.reactService.render(
			this.reactElement,
			RunThreadEntryPoint(props),
			getShadowDomMode(this.accountService?.email),
		);
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{didUpdate this.handleRunChange @runId}}
			{{willDestroy this.teardownView}}
			id="genie-routines-container"
			...attributes
		/>
	</template>
}
